import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Product, ProductAddPayload, productApi, ProductSyncPayload } from "../apis/fetchProductApi";

export const ProductSyncPayloadSchema = z.object({
    warehouseId: z.string().optional(), // optional string
    clientId: z.string().min(1, "Client ID is required"),
    superUserId: z.number().min(1, "Super User ID is required"),
    merchantId: z.string().min(1, "Merchant User ID is required"),
});

export const ProductAddPayloadSchema = z.object({
    categoryId: z.string().min(1, "Category ID is required"),
    subCategoryId: z.string().min(1, "Sub Category ID is required"),
    storageType: z.string().min(1, "Storage type is required"),
    capacity: z.string().optional(),
    clientId: z.string().min(1, "Client ID is required"),
    merchantId: z.string().min(1, "Merchant ID is required"),
    superUserId: z.number().min(1, "Super User ID is required"),
    warehouseId: z.string().min(1, "Warehouse ID is required"),
    items: z.array(
        z.object({
            productId: z.string().min(1, "Product ID is required"),
            name: z.string().min(1, "Product name is required"),
            sku: z.string().nullable(),
            price: z.number().min(0, "Price must be non-negative"),
            regularPrice: z.number().min(0, "Regular price must be non-negative"),
            thumbnail: z.string().nullable(),
            type: z.enum(["simple", "variant", "bundle"]), // Add type field
            variants: z
                .array(
                    z.object({
                        optionId: z.string(),
                        optionName: z.string(),
                        valueId: z.string(),
                        valueName: z.string(),
                        sku: z.string().nullable(),
                    })
                )
                .optional(),
            consistedProducts: z
                .array(
                    z.object({
                        productId: z.string(),
                        name: z.string(),
                        sku: z.string().nullable(),
                        price: z.number(),
                        quantity: z.number().optional(),
                        thumbnail: z.string().nullable(),
                    })
                )
                .optional(),
        })
    ),
});

const useCreateMutation = <T, P>(
    mutationFn: (payload: P, isFormData?: boolean) => Promise<any>,
    successMessage: string,
    invalidateKeys: string[] = [],
    validator?: z.ZodSchema<P>,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: P) => {
            if (validator) {
                validator.parse(payload);
            }
            const res = await mutationFn(payload);
            if (!res.success) {
                throw new Error(res.message || `${successMessage} failed`);
            }
            return res.data;
        },
        onSuccess: (data: T) => {
            console.log(`✅ ${successMessage}:`, data);
            invalidateKeys.forEach((key) =>
                queryClient.invalidateQueries({ queryKey: [key] }),
            );
        },
        onError: (error: Error) => {
            console.error(`❌ ${successMessage} failed:`, error.message);
        },
    });
};

export const useProductSyncMutation = (page: number, limit: number) =>
    useCreateMutation<Product, ProductSyncPayload>(
        (payload) => productApi.productSync(payload, page, limit),
        "Product fetched successfully",
        ["productList", "productAllData"],
        ProductSyncPayloadSchema,
    );


export const useProductAddMutation = () =>
    useCreateMutation<Product, ProductAddPayload>(
        productApi.productAdd,
        "Product Added successfully",
        ["productAdd", "productAddedData"],
        ProductAddPayloadSchema,
    );

